import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Plus, Edit2, Trash2, Home, Image, Users, Hash, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Cottages = () => {
  const { t } = useLanguage();
  const { token } = useAuth();
  
  const [cottages, setCottages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCottage, setSelectedCottage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    capacity: 4,
    image_url: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchCottages();
  }, []);

  const fetchCottages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cottages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCottages(response.data);
    } catch (error) {
      console.error('Error fetching cottages:', error);
    } finally {
      setLoading(false);
    }
  };

  const openNewCottage = () => {
    setSelectedCottage(null);
    setFormData({
      name: '',
      description: '',
      capacity: 4,
      image_url: '',
      order: cottages.length,
      is_active: true
    });
    setIsModalOpen(true);
  };

  const openEditCottage = (cottage) => {
    setSelectedCottage(cottage);
    setFormData({
      name: cottage.name,
      description: cottage.description || '',
      capacity: cottage.capacity,
      image_url: cottage.image_url || '',
      order: cottage.order,
      is_active: cottage.is_active
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCottage) {
        await axios.put(`${API_URL}/api/cottages/${selectedCottage.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/cottages`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setIsModalOpen(false);
      fetchCottages();
    } catch (error) {
      console.error('Error saving cottage:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kotteci silmək istədiyinizə əminsiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/cottages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCottages();
    } catch (error) {
      console.error('Error deleting cottage:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2D5016]"></div>
      </div>
    );
  }

  return (
    <div data-testid="cottages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A2415] font-['Cormorant_Garamond']">
            {t('admin_cottages')}
          </h1>
          <p className="text-[#4A5D43] text-sm mt-1">Kottecləri idarə edin</p>
        </div>
        <Button 
          onClick={openNewCottage}
          className="bg-[#2D5016] hover:bg-[#1A2415]"
          data-testid="add-cottage-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('cottage_add')}
        </Button>
      </div>

      {/* Cottages Grid */}
      {cottages.length === 0 ? (
        <div className="bg-white border border-[#E5E3DB] rounded-lg p-12 text-center">
          <Home className="w-16 h-16 text-[#E5E3DB] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#1A2415] mb-2">Hələ heç bir kottec yoxdur</h3>
          <p className="text-[#4A5D43] mb-4">İlk kotteci əlavə etmək üçün yuxarıdakı düyməyə klikləyin</p>
          <Button onClick={openNewCottage} className="bg-[#2D5016] hover:bg-[#1A2415]">
            <Plus className="w-4 h-4 mr-2" />
            {t('cottage_add')}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cottages.map((cottage) => (
            <div 
              key={cottage.id} 
              className={`bg-white border rounded-lg overflow-hidden transition-shadow hover:shadow-lg ${
                cottage.is_active ? 'border-[#E5E3DB]' : 'border-red-200 opacity-60'
              }`}
              data-testid={`cottage-card-${cottage.id}`}
            >
              {/* Image */}
              <div className="h-48 bg-[#F9F8F6] relative">
                {cottage.image_url ? (
                  <img 
                    src={cottage.image_url} 
                    alt={cottage.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-16 h-16 text-[#E5E3DB]" />
                  </div>
                )}
                
                {/* Status Badge */}
                <div className={`absolute top-3 right-3 px-2 py-1 rounded text-xs font-medium ${
                  cottage.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {cottage.is_active ? 'Aktiv' : 'Deaktiv'}
                </div>

                {/* Order Badge */}
                <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-[#2D5016] text-white flex items-center justify-center text-sm font-medium">
                  {cottage.order + 1}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-[#1A2415] mb-2">{cottage.name}</h3>
                
                <div className="flex items-center gap-4 text-sm text-[#4A5D43] mb-3">
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{cottage.capacity} {t('cottages_capacity')}</span>
                  </div>
                </div>

                {cottage.description && (
                  <p className="text-sm text-[#4A5D43] line-clamp-2 mb-4">{cottage.description}</p>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openEditCottage(cottage)}
                    className="flex-1"
                    data-testid={`edit-cottage-${cottage.id}`}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    {t('edit')}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(cottage.id)}
                    className="text-red-500 hover:text-red-700 hover:border-red-300"
                    data-testid={`delete-cottage-${cottage.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cottage Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedCottage ? 'Kotteci Redaktə Et' : t('cottage_add')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Home className="w-4 h-4" />
                {t('cottage_name')}
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Kottec 1"
                required
                data-testid="cottage-name-input"
              />
            </div>

            <div>
              <Label className="mb-2 block">{t('cottage_description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="cottage-description-input"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4" />
                {t('cottage_capacity')}
              </Label>
              <Input
                type="number"
                min="1"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                data-testid="cottage-capacity-input"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Image className="w-4 h-4" />
                {t('cottage_image')}
              </Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                data-testid="cottage-image-input"
              />
              {formData.image_url && (
                <div className="mt-2 h-32 bg-[#F9F8F6] rounded overflow-hidden">
                  <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Hash className="w-4 h-4" />
                {t('cottage_order')}
              </Label>
              <Input
                type="number"
                min="0"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                data-testid="cottage-order-input"
              />
              <p className="text-xs text-[#4A5D43] mt-1">Kiçik rəqəm = əvvəl göstərilir</p>
            </div>

            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                {formData.is_active ? <Check className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-red-500" />}
                {t('cottage_active')}
              </Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="cottage-active-switch"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-[#2D5016] hover:bg-[#1A2415]" data-testid="save-cottage-btn">
                {selectedCottage ? t('save') : t('add')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cottages;
